/**
 * Settings → Connections tab: the platforms agents run on, each a name, a base
 * URL and the secret the workbench signs in with. `?add=1` opens the add form,
 * `?edit=<id>` that connection's edit form (where "Reconnect" links lead).
 */

import { Plus } from "lucide-react";
import { useSearchParams } from "react-router";
import { Box } from "@/components/ui/box";
import { Button } from "@/components/ui/button";
import { useHolds } from "@/components/use-holds";
import { EmptyState, QueryState } from "@/components/ui/states";
import { useConnectors } from "@/features/connectors/hooks";
import { ConnectionDialog } from "./connection-dialog";
import { ConnectionRow } from "./connection-row";
import { useConnections } from "./hooks";
import { useAddParam } from "./use-add-param";

export function ConnectionsTab() {
  const connections = useConnections();
  const connectors = useConnectors();
  const [adding, setAdding] = useAddParam();
  const [params, setParams] = useSearchParams();
  const editing = connections.data?.find((c) => c.id === params.get("edit"));
  const stopEditing = () => setParams((p) => (p.delete("edit"), p));
  const label = (type: string) => connectors.data?.find((c) => c.type === type)?.label ?? type;
  const kindOf = (type: string) => connectors.data?.find((c) => c.type === type)?.kind;
  const admin = useHolds("connections:write");
  const addButton = admin ? (
    <Button variant="primary" onClick={() => setAdding(true)}>
      <Plus size={14} strokeWidth={2.2} aria-hidden />
      Add connection
    </Button>
  ) : null;
  return (
    <>
      <div className="flex items-center justify-between gap-4">
        <p className="text-[13px] text-muted">
          The platforms agents run on and the sources traces come from, and how the workbench signs in.
          {!admin && " Only workbench admins can add or change connections."}
        </p>
        {addButton}
      </div>
      <Box>
        <QueryState query={connections} what="connections">
          {connections.data?.length ? (
            <div className="overflow-x-auto">
              <table className="tbl">
                <thead>
                  <tr>
                    <th>Connection</th>
                    <th>Platform</th>
                    <th>Status</th>
                    <th aria-label="Actions" />
                  </tr>
                </thead>
                <tbody>
                  {connections.data.map((c) => (
                    <ConnectionRow
                      key={c.id}
                      connection={c}
                      connectorLabel={label(c.connector_type)}
                      kind={kindOf(c.connector_type)}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <EmptyState title="No connections yet" action={addButton}>
              {admin
                ? "Add one to run experiments: for Orchestrator, a base URL and a service client."
                : "Ask a workbench admin to add one before you run experiments."}
            </EmptyState>
          )}
        </QueryState>
      </Box>
      {admin && adding && <ConnectionDialog onClose={() => setAdding(false)} />}
      {admin && editing && <ConnectionDialog connection={editing} onClose={stopEditing} />}
    </>
  );
}
